import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'

import logo from '@assets/img/logos/logo-ether.png'
import { PrimaryButton } from '@components/buttons'
import DateTime from '@components/DateTime'
import { SpaceHorizontal } from '@components/Spacing'
import { Text } from '@components/texts'
import { AppBar, Box, Button, Icon, Menu, MenuItem, Toolbar } from '@material-ui/core'
import { AccountCircle } from '@material-ui/icons'
import { findMyInfos, logout } from '@reducers/authReducer'
import { Room, RoomType, WaitingRoomService } from '@services/api'
import { RootState } from '@state/store'
import { useDispatch, useSelector } from 'react-redux'

type Temp = { temp: number }

type OpenMeteoRes = {
  current?: {
    time: string
    interval: number
    temperature_2m?: number
    weather_code?: number
  }
}

// WMO weather code → Font Awesome icon
const WMO_ICON: Record<number, string> = {
  0: 'far fa-sun', // Clear sky
  1: 'fas fa-cloud-sun', // Mainly clear
  2: 'fas fa-cloud-sun', // Partly cloudy
  3: 'fas fa-cloud', // Overcast
  45: 'fas fa-smog', // Fog
  48: 'fas fa-smog', // Depositing rime fog
  51: 'fas fa-cloud-rain', // Drizzle: light
  53: 'fas fa-cloud-rain', // Drizzle: moderate
  55: 'fas fa-cloud-rain', // Drizzle: dense
  56: 'fas fa-cloud-rain', // Freezing drizzle: light
  57: 'fas fa-cloud-rain', // Freezing drizzle: dense
  61: 'fas fa-cloud-showers-heavy', // Rain: slight
  63: 'fas fa-cloud-showers-heavy', // Rain: moderate
  65: 'fas fa-cloud-showers-heavy', // Rain: heavy
  66: 'fas fa-cloud-showers-heavy', // Freezing rain: light
  67: 'fas fa-cloud-showers-heavy', // Freezing rain: heavy
  71: 'far fa-snowflake', // Snow fall: slight
  73: 'far fa-snowflake', // Snow fall: moderate
  75: 'far fa-snowflake', // Snow fall: heavy
  77: 'far fa-snowflake', // Snow grains
  80: 'fas fa-cloud-sun-rain', // Rain showers: slight
  81: 'fas fa-cloud-sun-rain', // Rain showers: moderate
  82: 'fas fa-cloud-sun-rain', // Rain showers: violent
  85: 'far fa-snowflake', // Snow showers: slight
  86: 'far fa-snowflake', // Snow showers: heavy
  95: 'fas fa-bolt', // Thunderstorm: slight/moderate
  96: 'fas fa-bolt', // Thunderstorm with slight hail
  99: 'fas fa-bolt', // Thunderstorm with heavy hail
}

export default function HeaderApp() {
  const history = useHistory()
  const dispatch = useDispatch()

  const user = useSelector(({ auth }: RootState) => auth.user)
  const selectedSite = useSelector(({ site }: RootState) => site.selectedSite)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorElMember, setAnchorElMember] = useState<null | HTMLElement>(null)

  const [loading, setLoading] = useState(false)

  const [temp, setTemp] = useState<Temp>()
  const [icon, setIcon] = useState<string>()

  useEffect(() => {
    dispatch(findMyInfos())
  }, [dispatch])

  const getCoords = async (): Promise<{ lat: number; lon: number }> => {
    const lat = (selectedSite as any)?.location?.lat
    const lon = (selectedSite as any)?.location?.lon
    if (lat != null && lon != null) return { lat, lon }

    const geo = await new Promise<GeolocationPosition | null>(resolve => {
      if (!navigator.geolocation) return resolve(null)
      navigator.geolocation.getCurrentPosition(
        pos => resolve(pos),
        () => resolve(null),
        { maximumAge: 10 * 60 * 1000, timeout: 5000 },
      )
    })
    if (geo) return { lat: geo.coords.latitude, lon: geo.coords.longitude }

    // Safi, Morocco (your fallback)
    return { lat: 32.277073, lon: -9.241674 }
  }

  const buildOpenMeteoUrl = async () => {
    const { lat, lon } = await getCoords()
    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lon),
      current: 'temperature_2m,weather_code',
      timezone: 'auto',
    })
    return `https://api.open-meteo.com/v1/forecast?${params.toString()}`
  }

  // Fetch weather on mount and when site changes; refresh every 10 minutes.
  useEffect(() => {
    const controller = new AbortController()

    const fetchWeather = async () => {
      try {
        const url = await buildOpenMeteoUrl()
        const res = await fetch(url, { signal: controller.signal })
        if (!res.ok) throw new Error(`Open-Meteo failed: ${res.status}`)
        const data = (await res.json()) as OpenMeteoRes

        const t = data.current?.temperature_2m
        const code = data.current?.weather_code
        if (typeof t === 'number') setTemp({ temp: t })
        setIcon(code != null ? WMO_ICON[code] || 'fas fa-cloud' : 'fas fa-cloud')
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err)
      }
    }

    fetchWeather()
    // refresh every 10 minutes
    const interval = window.setInterval(fetchWeather, 10 * 60 * 1000)

    return () => {
      controller.abort()
      if (interval) window.clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite])

  const rooms = useMemo(() => {
    if (!selectedSite) return []
    const { receptions, waitingRooms, offices, examRooms } = selectedSite
    const allRooms: (Room & { type: RoomType; path: string })[] = []
    receptions.forEach(room =>
      allRooms.push({ ...room, path: 'receptions', type: RoomType.Reception }),
    )
    waitingRooms.forEach(room =>
      allRooms.push({ ...room, path: 'waiting-rooms', type: RoomType.WaitingRoom }),
    )
    offices.forEach(room =>
      allRooms.push({ ...room, path: 'doctors', type: RoomType.DoctorOffice }),
    )
    examRooms.forEach(room =>
      allRooms.push({ ...room, path: 'operators', type: RoomType.ExamRoom }),
    )
    return allRooms
  }, [selectedSite])

  const handleClickItemRoom = (e: React.MouseEvent<HTMLElement>, id: number, type: RoomType) => {
    handleClose()
    const room = rooms.find(room => room.id === id && room.type === type)
    if (room) history.push(`/${room.path}/${id}`)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleClickMember = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorElMember(event.currentTarget)
  const handleCloseMember = () => setAnchorElMember(null)

  const handleLogout = () => dispatch(logout())

  const handleReset = async () => {
    if (!selectedSite) return
    setLoading(true)
    await WaitingRoomService.resetVisits({ siteId: selectedSite.id })
    setLoading(false)
    window.location.reload()
  }

  const memberMenuId = 'member-menu'
  const memberMenu = (
    <Menu
      id={memberMenuId}
      anchorEl={anchorElMember}
      keepMounted
      open={Boolean(anchorElMember)}
      onClose={handleCloseMember}
    >
      <MenuItem value="logout" onClick={handleLogout}>
        Logout
      </MenuItem>
    </Menu>
  )

  const roomMenuId = 'room-menu'
  const roomMenu = (
    <Menu
      id={roomMenuId}
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
    >
      {rooms.map((option, i) => (
        <MenuItem
          key={i}
          value={option.id}
          onClick={e => handleClickItemRoom(e, option.id, option.type)}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  )

  return (
    <AppBar position="static">
      <Toolbar
        style={{
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignContent: 'center',
        }}
      >
        <Box display="flex" alignItems="center" alignContent="flex-start">
          <img src={logo} alt="logo" style={{ width: '130px' }} />
        </Box>

        <Box display="flex" alignItems="center" alignContent="center">
          <DateTime />
          <SpaceHorizontal size={40} />
          <Icon className={icon} style={{ color: '#3f3f3f' }} />
          <SpaceHorizontal size={20} />
          <Text text={`${Math.round(temp?.temp ?? 0)}º`} style={{ color: '#3f3f3f' }} />
        </Box>

        <Box display="flex" alignItems="center" alignContent="flex-start">
          <PrimaryButton
            type="reset"
            text="Reset demo"
            fullWidth
            color="secondary"
            onClick={handleReset}
            loading={loading}
            style={{ marginRight: '20px' }}
          />
          <Button
            aria-controls={roomMenuId}
            aria-haspopup="true"
            onClick={handleClick}
            startIcon={
              <Icon
                className="fas fa-cube"
                fontSize="default"
                style={{ fontSize: '25px', color: '#3f3f3f' }}
              />
            }
          />
          <Button
            aria-controls={memberMenuId}
            aria-haspopup="true"
            onClick={handleClickMember}
            startIcon={<AccountCircle style={{ fontSize: '28px', color: '#3f3f3f' }} />}
          >
            {user?.firstName}
          </Button>
        </Box>
      </Toolbar>

      {roomMenu}
      {memberMenu}
    </AppBar>
  )
}
